import { createSelector, createSlice } from "@reduxjs/toolkit";
import { logout } from "../common/reducers/initContext";
import { dataHookFactory, initMapFactory, DefaultFailureAction, DefaultSuccessAction } from "../common/reducers/utilities";
import { ensureArray, groupBy } from "../common/utils/arrayUtilities";
import { parseDate } from "../common/utils/dateUtils";
import { markFetched, registerEntityBasedFetch } from "../common/hooks/initHook";

const key = mq => `${mq.memberid}_${mq.qualificationid}`;

const memberQualificationSlice = createSlice({
    name: "memberQualifications",
    initialState: {
        data: {}
    },
    reducers: {
        setByMember: (state, action) => {
            const { memberid, data } = action.payload;
            const toRemove = new Set(Object.keys(state.data).filter(key => key.startsWith(`${memberid}_`)));
            if (data?.length >= 0) {
                data.forEach(mq => {
                    state.data[key(mq)] = mq;
                    toRemove.delete(key(mq));
                })
            }
            toRemove.forEach(key => delete state.data[key]);
        },
        setByQual: (state, action) => {
            const { qualificationid, data } = action.payload;
            const toRemove = new Set(Object.keys(state.data).filter(key => key.endsWith(`_${qualificationid}`)));
            if (data?.length >= 0) {
                data.forEach(mq => {
                    state.data[key(mq)] = mq
                    toRemove.delete(key(mq));
                });
            }
            toRemove.forEach(key => delete state.data[key]);
        },
        setByExpiry: (state, action) => {
            const expiryDate = new Date(action.payload.expiry);
            const toRemove = new Set(Object.values(state.data).filter(mq => new Date(mq.expirydate) > expiryDate).map(mq => key(mq)));
            action.payload.data.forEach(mq => {
                state.data[key(mq)] = mq;
                toRemove.delete(key(mq));
            });
            toRemove.forEach(key => delete state.data[key]);
        },
        addOrUpdate: (state, action) => {
            ensureArray(action.payload).forEach(mq => state.data[key(mq)] = mq)
        },
        remove: (state, action) => {
            ensureArray(action.payload).forEach(mq => delete state.data[key(mq)])
        }
    },
    extraReducers: builder => {
        builder.addCase(logout, () => memberQualificationSlice.getInitialState());
    }
});

const fullRoles = ['administrator', 'training manager', 'member manager'];
const readRoles = ['auditor', 'training auditor', 'member auditor', 'accreditation auditor'];
export const canViewMemberQualifications = credentials => credentials.hasRole(fullRoles) || credentials.hasRole(readRoles);

export const canUpdateMemberQualifications = (credentials, member) => checkPermissions(credentials, member, fullRoles, !member.inactive);
export const canCreateMemberQualifications = canUpdateMemberQualifications;
export const canRemoveMemberQualifications = canUpdateMemberQualifications;
export const canRouteMemberQualifications = ({credentials}) => canViewMemberQualifications(credentials);
export const canRouteStaffMemberQualifications = ({credentials, clientConfig}) => !clientConfig.disableMemberQualUpload && credentials.hasRole(['member']);

const checkPermissions = (credentials, member, requiredRoles, condition) => {
    const access = credentials.hasRole(requiredRoles, {office: member?.office, client: member?.default_client, location: member?.default_location});
    return (access && condition !== false && !member?.inactive) ?? false 
}

// #region network operations
const visualizeSuccess = (verb, action) => DefaultSuccessAction(verb, 'staffQualifications', action);
const visualizeFailure = verb => DefaultFailureAction(verb, 'staffQualifications');

const fetchByMember = memberid => network => network.get('/api/v1/memberqual/' + encodeURIComponent(memberid), data => memberQualificationSlice.actions.setByMember({ memberid, data }), visualizeFailure('get'));
const fetchByQual = qualificationid => network => network.post('/api/v1/qualAuditing', { qualificationid }, data => memberQualificationSlice.actions.setByQual({ qualificationid, data }), visualizeFailure('get'));
const fetchByExpiry = expiry => network => network.get('/api/v1/memberqualByExpiry/' + encodeURIComponent(expiry), data => memberQualificationSlice.actions.setByExpiry({ expiry, data }), visualizeFailure('get'));

export const createMemberQualification = (network, mq) => network.post('/api/v1/memberqual', mq, memberQualificationSlice.actions.addOrUpdate, visualizeFailure('create'));
export const updateMemberQualification = (network, delta) => network.put('/api/v1/memberqual', delta, memberQualificationSlice.actions.addOrUpdate, visualizeFailure('update'));
export const removeMemberQualification = (network, memberid, qualificationid) => network.delete('/api/v1/memberqual/' + encodeURIComponent(memberid) + '/' + encodeURIComponent(qualificationid), memberQualificationSlice.actions.remove, visualizeFailure('remove'));
// #endregion

// #region selectors
const selectData = state => state.memberQualifications.data;
const selectMemberData = createSelector([selectData], data => groupBy(Object.values(data), 'memberid'));
const selectQualData = createSelector([selectData], data => groupBy(Object.values(data), 'qualificationid'));
const selectByMember = memberid => createSelector([selectMemberData], memberData => memberData[memberid] ?? []);
const selectByQual = qualificationid => createSelector([selectQualData], qualData => qualData[qualificationid] ?? []);
const selectByExpiry = expiry => createSelector([selectData], qualData => {
    const expiryDate = parseDate(expiry);
    return Object.values(qualData).filter(mq => parseDate(mq.expirydate) < expiryDate);
})
// #endregion

const listen = {
    memberQualCreated: (mq, { dispatch, websocket }) => {
        markFetched(websocket, 'member_qualification', mq.memberid, mq.qualificationid)
        dispatch(memberQualificationSlice.actions.addOrUpdate(mq))
    },
    memberQualUpdated: (mq, { dispatch }) => dispatch(memberQualificationSlice.actions.addOrUpdate(mq)),
    memberQualRemoved: (mq, { dispatch }) => dispatch(memberQualificationSlice.actions.remove(mq))
}
const cleanup = { root: memberQualificationSlice.name, all: state => Object.values(selectData).flat(), removeAll: memberQualificationSlice.actions.remove, key };
const initQualsByMemberID = initMapFactory(fetchByMember, listen, { name: 'memberQualsForMember' });
registerEntityBasedFetch('member', initQualsByMemberID);

export const [useMemberQualificationsForMember] = dataHookFactory(selectByMember, initQualsByMemberID, { cleanup });

const initQualsByQualID = initMapFactory(fetchByQual, listen);
registerEntityBasedFetch('qualification', initQualsByQualID);

export const [useMemberQualfiicationsForQualification] = dataHookFactory(selectByQual, initQualsByQualID, { cleanup });
export const [useMemberQualificationsByExpiry] = dataHookFactory(selectByExpiry, initMapFactory(fetchByExpiry, listen), { cleanup });

const reducer = { name: memberQualificationSlice.name, reducer: memberQualificationSlice.reducer }
export default reducer;